import "./Anova.css";

import {
  AD_COL_CLASSES,
  ANALYTICS_EVENTS,
  NULL_GROUP_LABEL,
} from "../../../constants";
import { Badge, Button, Col, Row } from "reactstrap";
import { addAnova, addTukey } from "../../../actions/resultMap";
import { requestAnova, requestTukey } from "../../../services/backendRequests";
import { useDispatch, useSelector } from "react-redux";
import { useMemo, useRef, useState } from "react";

import { AnovaHistory } from "./AnovaHistory/AnovaHistory";
import { AnovaTable } from "./components/AnovaTable/AnovaTable";
import { Chart } from "../../components/charts/Chart";
import { ControlSection } from "../../components/ControlSection/ControlSection";
import { HELP_TOOLTIPS } from "../../../helpTooltips/helpTooltips";
import { HelpTooltip } from "../../components/HelpTooltip/HelpTooltip";
import { InlineSpinner } from "../../../components/InlineSpinner";
import { LayerIdentifier } from "../../../components/LayerIdentifier/LayerIdentifier";
import { LinearModelDefiner } from "./components/LinearModelDefiner/LinearModelDefiner";
import { OffCanvasWrapper } from "../../components/advancedFiltering/OffCanvasWrapper";
import { QQPlot } from "./components/QQPlot/QQPlot";
import { Timeline } from "../../components/Timeline/Timeline";
import { Tukey } from "./components/Tukey/Tukey";
import { addDangerAlert } from "../../../actions/alerts";
import { selectFilteredFeatures } from "../../../selectors/resultMap";
import { useTracking } from "../../../analytics";

export const Anova = () => {
  const user = useSelector((state) => state.user);
  const [
    trial,
    selectedDate,
    selectedTrait,
    distinctExperiments,
    currentAnova,
  ] = useSelector(({ resultMap }) => [
    resultMap.trial,
    resultMap.trial_date,
    resultMap.selectedTrait,
    resultMap.distinctExperiments,
    resultMap.anova.previousAnovas[resultMap.anova.selectedAnovaIndex],
  ]);

  const features = useSelector(selectFilteredFeatures);

  const dispatch = useDispatch();

  const experimentsOptions = useMemo(
    () =>
      distinctExperiments.map((experiment) => ({
        value: experiment,
        label: experiment ?? NULL_GROUP_LABEL,
      })),
    [distinctExperiments]
  );

  const [selectedExperiment, setSelectedExperiment] = useState(
    experimentsOptions[0]
  );
  const [factors, setFactors] = useState([]);
  const [anovaIsLoading, setAnovaIsLoading] = useState(false);
  const [tukeyIsLoading, setTukeyIsLoading] = useState(false);
  const [displayedTukey, setDisplayedTukey] = useState();

  const [isOffcanvasOpen, setIsOffcanvasOpen] = useState(false);
  const toggleOffcanvas = () => {
    setIsOffcanvasOpen(!isOffcanvasOpen);
    trackEvent(ANALYTICS_EVENTS.ANALYTICS_AF_BUTTON);
  };

  // This is the list of properties included in the factors including interactions.
  const requiredProperties = useMemo(
    () =>
      Array.from(
        new Set(
          [selectedTrait.technical_name, ...factors.map((f) => f ?? [])].flat()
        )
      ),
    [factors, selectedTrait.technical_name]
  );

  const { trackEvent } = useTracking();

  const handleAnovaRequest = async () => {
    trackEvent(ANALYTICS_EVENTS.COMPUTE_ANOVA);
    setAnovaIsLoading(true);
    setTukeyIsLoading(true);

    const linearModel = `${selectedTrait.technical_name}~${factors
      .map((f) => f.join("*"))
      .join("+")}`;
    const experimentName = selectedExperiment?.value ?? "";

    // Data contains factors and response for each feature of selected experiment
    const data = [];
    features.forEach(({ experiment, id, properties }) => {
      if (experiment !== experimentName) return;
      const featureData = {
        id: id,
        ...requiredProperties.reduce(
          (acc, key) => ({
            ...acc,
            [key]: properties[key],
          }),
          {}
        ),
      };
      // Remove features that does only contain null properties
      if (Object.values(featureData).some((v) => v !== null))
        data.push(featureData);
    });

    const anovaId = Date.now(); // timestamp id generated to link anova and tukey within store
    const params = [
      user,
      linearModel,
      data,
      selectedExperiment?.value ?? NULL_GROUP_LABEL,
      requiredProperties,
      selectedDate,
    ];

    // Request anova and tukey lambdas execution
    await Promise.all([
      requestAnova(...params)
        .then((anova) => {
          dispatch(addAnova({ id: anovaId, ...anova }));
          setAnovaIsLoading(false);
        })
        .catch(() => {
          setAnovaIsLoading(false);
          dispatch(
            addDangerAlert("Something went wrong when calculating ANOVA")
          );
        }),

      requestTukey(...params)
        .then(({ tukey, tukey_errors }) => {
          setTukeyIsLoading(false);
          dispatch(addTukey(anovaId, tukey, tukey_errors));
        })
        .catch(() => {
          setTukeyIsLoading(false);
          dispatch(
            addDangerAlert("Something went wrong when calculating Tukey HSD")
          );
        }),
    ]);
  };

  const displayTukey = (key) => {
    trackEvent(ANALYTICS_EVENTS.DISPLAY_TUKEY_HSD);
    setDisplayedTukey(key);
  };

  const closeTukey = () => {
    setDisplayedTukey();
  };

  // Close Tukey HSD on anova change
  const prevAnovaId = useRef();
  if (currentAnova?.id !== prevAnovaId.current) {
    closeTukey();
    prevAnovaId.current = currentAnova?.id;
  }

  return (
    <Row className="anova g-0">
      <Col className={AD_COL_CLASSES} sm="2">
        <ControlSection alwaysExpanded />
        <OffCanvasWrapper
          isOffcanvasOpen={isOffcanvasOpen}
          toggleOffcanvas={toggleOffcanvas}
        />
        <LinearModelDefiner
          factors={factors}
          setFactors={setFactors}
          experimentsOptions={experimentsOptions}
          selectedExperiment={selectedExperiment}
          setSelectedExperiment={setSelectedExperiment}
        />
        {trial && (
          <div className="powerdash-component anova-settings gap-1">
            <LayerIdentifier />
            <Button
              color="success"
              className="d-flex align-items-baseline justify-content-between"
              onClick={handleAnovaRequest}
              disabled={
                anovaIsLoading ||
                !factors.length ||
                requiredProperties.includes(null) ||
                requiredProperties.includes("")
              }
            >
              Compute Anova
              {anovaIsLoading ? (
                <InlineSpinner className="ms-1" />
              ) : (
                <Badge className="hiphen-badge small">{features.length}</Badge>
              )}
            </Button>
          </div>
        )}
      </Col>
      <Col className={AD_COL_CLASSES}>
        {trial && (
          <Row className="g-0">
            <Col className={`${AD_COL_CLASSES} pe-5`}>
              <Timeline dates={trial.trial_dates} selectedDate={selectedDate} />
            </Col>
          </Row>
        )}
        <Row className="h-100 g-0">
          <Col className={AD_COL_CLASSES} sm="8">
            <AnovaTable
              computedAnova={currentAnova}
              displayedTukey={displayedTukey}
              displayTukey={displayTukey}
            />
            <Tukey
              computedResult={currentAnova}
              displayedTukey={displayedTukey}
              closeTukey={closeTukey}
              tukeyIsLoading={tukeyIsLoading}
            />
          </Col>
          <Col className={AD_COL_CLASSES} sm="4">
            <Chart
              icon="fa-solid fa-q"
              title="Quantile-Quantile plot"
              topRightContent={
                <HelpTooltip helpTooltip={HELP_TOOLTIPS.QQ_PLOT} />
              }
            >
              {currentAnova && !currentAnova.anova_error && (
                <QQPlot residuals={currentAnova.residuals} />
              )}
            </Chart>
            <AnovaHistory />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
